import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash, PaperPlaneTilt, CheckCircle, XCircle,
  ArrowRight, Eye, CaretDown, Package, Cube, ClockCounterClockwise,
  MagnifyingGlass, Warning, Calculator, ArrowCounterClockwise,
  DownloadSimple, UploadSimple, Sparkle, CircleNotch,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  draft: 'bg-zinc-100 text-zinc-600',
  under_review: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  published: 'bg-blue-100 text-blue-700',
};

const STATUS_LABELS = {
  draft: 'Draft', under_review: 'Under Review', approved: 'Approved', published: 'Published',
};

const Badge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${STATUS_COLORS[status] || 'bg-zinc-100 text-zinc-500'}`} data-testid={`badge-${status}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

/* ═══════════════════════════════════════════════════ */
/*  RECIPE EDITOR (shared between Items & Assemblies) */
/* ═══════════════════════════════════════════════════ */

const MaterialRecipeEditor = ({ recipes, onChange, materials, onMaterialCreated }) => {
  const [showNewMat, setShowNewMat] = useState(false);
  const [newMatForm, setNewMatForm] = useState({ description: '', category: '', unit: 'Nos', family: '' });
  const [newMatDupes, setNewMatDupes] = useState({ duplicates: [], has_warning: false });
  const [newMatFamilies, setNewMatFamilies] = useState([]);
  const [newMatCategories, setNewMatCategories] = useState([]);
  const [creatingMat, setCreatingMat] = useState(false);

  const add = () => onChange([...recipes, { material_id: '', material_name: '', material_code: '', unit: '', quantity_per_unit: 0, wastage_percentage: 0 }]);
  const remove = (i) => onChange(recipes.filter((_, idx) => idx !== i));
  const update = (i, field, value) => {
    const next = [...recipes];
    next[i] = { ...next[i], [field]: value };
    if (field === 'material_id' && value) {
      const mat = materials.find(m => (m.id || m._id) === value);
      if (mat) {
        next[i].material_name = mat.description || mat.material_name || '';
        next[i].material_code = mat.material_code || '';
        next[i].unit = mat.unit || '';
      }
    }
    onChange(next);
  };

  const openNewMaterial = async () => {
    setNewMatForm({ description: '', category: '', unit: 'Nos', family: '' });
    setNewMatDupes({ duplicates: [], has_warning: false });
    try {
      const [famRes, catRes] = await Promise.all([
        axios.get(`${API}/api/material-master/families`, { withCredentials: true }),
        axios.get(`${API}/api/material-master/categories`, { withCredentials: true }),
      ]);
      setNewMatFamilies(famRes.data.families || []);
      setNewMatCategories(catRes.data.categories || []);
    } catch { /* use empty */ }
    setShowNewMat(true);
  };

  const checkNewMatDupes = async (desc) => {
    if (!desc || desc.length < 3) return;
    try {
      const { data } = await axios.post(`${API}/api/material-master/check-duplicate`, {
        description: desc, category: newMatForm.category, family: newMatForm.family,
      }, { withCredentials: true });
      setNewMatDupes(data);
    } catch { /* ignore */ }
  };

  const selectExistingFromDupe = (dupeId) => {
    const mat = materials.find(m => (m.id || m._id) === dupeId);
    if (mat) {
      // Add as last row
      onChange([...recipes, { material_id: mat.id || mat._id, material_name: mat.description || '', material_code: mat.material_code || '', unit: mat.unit || '', quantity_per_unit: 0, wastage_percentage: 0 }]);
    }
    setShowNewMat(false);
  };

  const createNewMaterial = async () => {
    if (!newMatForm.description.trim() || !newMatForm.category) { toast.error('Description and Category are required'); return; }
    setCreatingMat(true);
    try {
      const { data } = await axios.post(`${API}/api/material-master`, newMatForm, { withCredentials: true });
      toast.success(`Created ${data.material_code}`);
      // Add to recipe
      onChange([...recipes, { material_id: data.id, material_name: data.description, material_code: data.material_code, unit: data.unit, quantity_per_unit: 0, wastage_percentage: 0 }]);
      if (onMaterialCreated) onMaterialCreated();
      setShowNewMat(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create material'); }
    setCreatingMat(false);
  };

  return (
    <div data-testid="material-recipe-editor">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[11px] font-bold text-zinc-600 uppercase tracking-wide">Material Recipe</h4>
        <div className="flex gap-2">
          <button onClick={openNewMaterial} className="text-[10px] text-emerald-600 hover:text-emerald-800 font-semibold flex items-center gap-1" data-testid="new-material-inline">
            <Plus size={12} /> New Material
          </button>
          <button onClick={add} className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1" data-testid="add-material-row">
            <Plus size={12} /> Add Row
          </button>
        </div>
      </div>

      {/* Inline new material mini-form */}
      {showNewMat && (
        <div className="border border-emerald-300 bg-emerald-50/50 rounded p-3 mb-3 space-y-2" data-testid="inline-new-material-form">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-emerald-700 uppercase">Create New Material</span>
            <button onClick={() => setShowNewMat(false)} className="text-zinc-400 hover:text-zinc-600"><XCircle size={14} /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={newMatForm.description} onChange={e => { setNewMatForm({...newMatForm, description: e.target.value}); checkNewMatDupes(e.target.value); }}
              placeholder="Material name *" className="border border-zinc-200 rounded px-2 py-1.5 text-[11px] col-span-2" data-testid="inline-mat-name" />
            <select value={newMatForm.category} onChange={e => setNewMatForm({...newMatForm, category: e.target.value})} className="border border-zinc-200 rounded px-2 py-1.5 text-[11px]" data-testid="inline-mat-category">
              <option value="">Category *</option>
              {newMatCategories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={newMatForm.family} onChange={e => setNewMatForm({...newMatForm, family: e.target.value})} className="border border-zinc-200 rounded px-2 py-1.5 text-[11px]" data-testid="inline-mat-family">
              <option value="">Family</option>
              {newMatFamilies.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <input value={newMatForm.unit} onChange={e => setNewMatForm({...newMatForm, unit: e.target.value})} placeholder="Unit" className="border border-zinc-200 rounded px-2 py-1.5 text-[11px]" />
          </div>
          {/* Show duplicate matches */}
          {newMatDupes.duplicates?.length > 0 && (
            <div className={`border rounded p-2 ${newMatDupes.has_warning ? 'border-amber-400 bg-amber-50' : 'border-zinc-200 bg-white'}`}>
              <p className="text-[10px] font-bold text-amber-700 mb-1">{newMatDupes.has_warning ? 'Similar materials exist:' : 'Possible matches:'}</p>
              {newMatDupes.duplicates.slice(0, 3).map((d, i) => (
                <div key={i} className="flex items-center justify-between text-[10px] py-1 border-b border-zinc-100 last:border-0">
                  <span><span className="font-mono text-blue-700 font-bold mr-1">{d.material_code}</span> {d.description}</span>
                  <button onClick={() => selectExistingFromDupe(d.id)} className="text-blue-600 hover:text-blue-800 font-bold text-[9px] px-1.5 py-0.5 border border-blue-300 rounded" data-testid={`use-existing-${i}`}>
                    Use This
                  </button>
                </div>
              ))}
            </div>
          )}
          <button onClick={createNewMaterial} disabled={creatingMat} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold py-1.5 rounded disabled:opacity-50" data-testid="confirm-create-material">
            {creatingMat ? 'Creating...' : 'Confirm & Add to Recipe'}
          </button>
        </div>
      )}

      {recipes.length === 0 && !showNewMat && <p className="text-[11px] text-zinc-400 italic">No materials added</p>}
      {recipes.length > 0 && (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-zinc-200">
              <th className="text-left py-1.5 text-zinc-400 font-semibold">Material</th>
              <th className="text-left py-1.5 text-zinc-400 font-semibold w-16">Unit</th>
              <th className="text-right py-1.5 text-zinc-400 font-semibold w-20">Qty/Unit</th>
              <th className="text-right py-1.5 text-zinc-400 font-semibold w-20">Wastage%</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {recipes.map((r, i) => (
              <tr key={i} className="border-b border-zinc-50">
                <td className="py-1.5">
                  <select value={r.material_id} onChange={e => update(i, 'material_id', e.target.value)}
                    className="w-full border border-zinc-200 rounded px-2 py-1 text-[11px]" data-testid={`mat-select-${i}`}>
                    <option value="">Select material...</option>
                    {materials.map(m => (
                      <option key={m.id || m._id} value={m.id || m._id}>
                        {m.material_code} — {m.description || m.material_name}
                      </option>
                    ))}
                  </select>
                  {r.material_name && (
                    <span className="text-[10px] text-zinc-400 mt-0.5 block" data-testid={`mat-name-${i}`}>{r.material_name}</span>
                  )}
                </td>
                <td className="py-1.5">
                  <input value={r.unit || ''} onChange={e => update(i, 'unit', e.target.value)}
                    placeholder="Unit" className="w-full border border-zinc-200 rounded px-2 py-1 text-[11px]" data-testid={`mat-unit-${i}`} />
                </td>
                <td className="py-1.5">
                  <input type="number" value={r.quantity_per_unit} onChange={e => update(i, 'quantity_per_unit', parseFloat(e.target.value) || 0)}
                    className="w-full border border-zinc-200 rounded px-2 py-1 text-[11px] text-right" data-testid={`mat-qty-${i}`} />
                </td>
                <td className="py-1.5">
                  <input type="number" value={r.wastage_percentage} onChange={e => update(i, 'wastage_percentage', parseFloat(e.target.value) || 0)}
                    className="w-full border border-zinc-200 rounded px-2 py-1 text-[11px] text-right" />
                </td>
                <td className="py-1.5 text-center">
                  <button onClick={() => remove(i)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const ManpowerRecipeEditor = ({ recipes, onChange }) => {
  const add = () => onChange([...recipes, {
    labour_type: 'Skilled', role_name: '',
    manpower_per_unit: 0, output_per_day: 0,
    metric_input: 'output_per_day',
    unit_basis: 'per_boq_unit',
  }]);
  const remove = (i) => onChange(recipes.filter((_, idx) => idx !== i));
  const update = (i, field, value) => {
    const next = [...recipes];
    const r = { ...next[i], [field]: value };
    // Keep MPU & OPD in lock-step
    if (field === 'manpower_per_unit') {
      r.metric_input = 'manpower_per_unit';
      r.output_per_day = value > 0 ? Math.round((1 / value) * 1e6) / 1e6 : 0;
    } else if (field === 'output_per_day') {
      r.metric_input = 'output_per_day';
      r.manpower_per_unit = value > 0 ? Math.round((1 / value) * 1e6) / 1e6 : 0;
    }
    next[i] = r;
    onChange(next);
  };

  return (
    <div data-testid="manpower-recipe-editor">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[11px] font-bold text-zinc-600 uppercase tracking-wide">Manpower Recipe</h4>
        <button onClick={add} className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1" data-testid="add-manpower-row">
          <Plus size={12} /> Add Role
        </button>
      </div>
      <p className="text-[10px] text-zinc-500 mb-1">
        Enter <strong>EITHER</strong> Output/Day (units a worker makes in 1 day) <strong>OR</strong> MP/Unit (man-days per unit). The other auto-computes.
      </p>
      {recipes.length === 0 && <p className="text-[11px] text-zinc-400 italic">No manpower added</p>}
      {recipes.length > 0 && (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-zinc-200">
              <th className="text-left py-1.5 text-zinc-400 font-semibold">Role Name</th>
              <th className="text-left py-1.5 text-zinc-400 font-semibold w-24">Labour Type</th>
              <th className="text-right py-1.5 text-zinc-400 font-semibold w-24" title="Units a worker can produce in 1 worker-day">Output/Day</th>
              <th className="text-right py-1.5 text-zinc-400 font-semibold w-24" title="Man-days per item-unit (1 / Output/Day)">MP/Unit</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {recipes.map((r, i) => (
              <tr key={i} className="border-b border-zinc-50">
                <td className="py-1.5">
                  <input value={r.role_name} onChange={e => update(i, 'role_name', e.target.value)}
                    placeholder="e.g. Carpenter" className="w-full border border-zinc-200 rounded px-2 py-1 text-[11px]" data-testid={`mp-role-${i}`} />
                </td>
                <td className="py-1.5">
                  <select value={r.labour_type} onChange={e => update(i, 'labour_type', e.target.value)}
                    className="w-full border border-zinc-200 rounded px-2 py-1 text-[11px]">
                    <option value="Skilled">Skilled</option>
                    <option value="Unskilled">Unskilled</option>
                    <option value="Supervisor">Supervisor</option>
                  </select>
                </td>
                <td className="py-1.5">
                  <input
                    type="number"
                    value={r.output_per_day || 0}
                    onChange={e => update(i, 'output_per_day', parseFloat(e.target.value) || 0)}
                    className={`w-full border rounded px-2 py-1 text-[11px] text-right ${r.metric_input === 'output_per_day' ? 'border-blue-300 bg-blue-50' : 'border-zinc-200'}`}
                    step="any"
                    placeholder="200"
                    data-testid={`mp-output-${i}`}
                  />
                </td>
                <td className="py-1.5">
                  <input
                    type="number"
                    value={r.manpower_per_unit || 0}
                    onChange={e => update(i, 'manpower_per_unit', parseFloat(e.target.value) || 0)}
                    className={`w-full border rounded px-2 py-1 text-[11px] text-right ${r.metric_input === 'manpower_per_unit' ? 'border-blue-300 bg-blue-50' : 'border-zinc-200'}`}
                    step="any"
                    placeholder="0.005"
                    data-testid={`mp-qty-${i}`}
                  />
                </td>
                <td className="py-1.5 text-center">
                  <button onClick={() => remove(i)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ═══════════════════════════════════ */
/*  COST CALCULATOR PANEL              */
/* ═══════════════════════════════════ */

const CostCalculator = ({ materialRecipe, manpowerRecipe }) => {
  const [costs, setCosts] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/studio/cost-calculator`, {
        material_recipe: materialRecipe || [],
        manpower_recipe: manpowerRecipe || [],
      }, { withCredentials: true });
      setCosts(data);
    } catch { toast.error('Cost calculation failed.'); }
    setLoading(false);
  };

  const fmtRs = (v) => v != null ? `Rs ${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—';

  return (
    <div className="border border-dashed border-zinc-300 rounded-lg p-4 bg-zinc-50/50" data-testid="cost-calculator">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-[11px] font-bold text-zinc-600 uppercase tracking-wide flex items-center gap-1.5">
          <Calculator size={14} className="text-amber-600" /> Recipe Cost Intelligence
        </h4>
        <button onClick={calculate} disabled={loading || ((!materialRecipe || materialRecipe.length === 0) && (!manpowerRecipe || manpowerRecipe.length === 0))}
          className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 disabled:text-zinc-300 flex items-center gap-1" data-testid="calc-costs-btn">
          {loading ? 'Calculating...' : 'Calculate Costs'}
        </button>
      </div>

      {!costs && !loading && (
        <p className="text-[11px] text-zinc-400 italic">Add recipes above, then click "Calculate Costs" for advisory pricing</p>
      )}

      {costs && (
        <div className="space-y-3">
          {/* Material costs */}
          {costs.material_costs?.length > 0 && (
            <div>
              <span className="text-[10px] text-zinc-500 font-semibold uppercase">Material Cost Breakdown</span>
              <table className="w-full text-[11px] mt-1">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-1.5 text-zinc-400 font-semibold">Material</th>
                    <th className="text-right py-1.5 text-zinc-400 font-semibold w-16">Eff. Qty</th>
                    <th className="text-right py-1.5 text-zinc-400 font-semibold w-20">Latest PO</th>
                    <th className="text-right py-1.5 text-zinc-400 font-semibold w-20">Avg (3 PO)</th>
                    <th className="text-right py-1.5 text-zinc-400 font-semibold w-20">Cost/Unit</th>
                    <th className="text-center py-1.5 text-zinc-400 font-semibold w-16">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.material_costs.map((m, i) => (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="py-1.5 text-zinc-700">{m.material_name}</td>
                      <td className="py-1.5 text-right text-zinc-600">{m.effective_qty}</td>
                      <td className="py-1.5 text-right text-zinc-700 font-medium">{m.latest_po_rate != null ? fmtRs(m.latest_po_rate) : <span className="text-zinc-400">No data</span>}</td>
                      <td className="py-1.5 text-right text-zinc-600">{m.avg_rate_last_3 != null ? fmtRs(m.avg_rate_last_3) : '—'}</td>
                      <td className="py-1.5 text-right font-semibold text-zinc-800">{m.derived_cost_per_unit > 0 ? fmtRs(m.derived_cost_per_unit) : '—'}</td>
                      <td className="py-1.5 text-center">
                        {m.po_count > 0 ? (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${m.match_confidence >= 90 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {m.match_confidence}%
                          </span>
                        ) : (
                          <span className="text-[9px] text-zinc-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Manpower costs */}
          {costs.manpower_costs?.length > 0 && (
            <div>
              <span className="text-[10px] text-zinc-500 font-semibold uppercase">Manpower Cost Estimate</span>
              <table className="w-full text-[11px] mt-1">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left py-1.5 text-zinc-400 font-semibold">Role</th>
                    <th className="text-left py-1.5 text-zinc-400 font-semibold w-20">Type</th>
                    <th className="text-right py-1.5 text-zinc-400 font-semibold w-16">MP/Unit</th>
                    <th className="text-right py-1.5 text-zinc-400 font-semibold w-20">Daily Rate</th>
                    <th className="text-right py-1.5 text-zinc-400 font-semibold w-20">Cost/Unit</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.manpower_costs.map((mp, i) => (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="py-1.5 text-zinc-700">{mp.role_name}</td>
                      <td className="py-1.5 text-zinc-600">{mp.labour_type}</td>
                      <td className="py-1.5 text-right text-zinc-600">{mp.manpower_per_unit}</td>
                      <td className="py-1.5 text-right text-zinc-600">{fmtRs(mp.daily_rate_reference)}</td>
                      <td className="py-1.5 text-right font-semibold text-zinc-800">{fmtRs(mp.derived_cost_per_unit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Totals */}
          <div className="bg-white border border-zinc-200 rounded p-3 grid grid-cols-3 gap-4">
            <div>
              <div className="text-[9px] text-zinc-400 uppercase font-semibold">Material Cost</div>
              <div className="text-sm font-bold text-zinc-800">{fmtRs(costs.total_material_cost_per_unit)}</div>
            </div>
            <div>
              <div className="text-[9px] text-zinc-400 uppercase font-semibold">Manpower Cost</div>
              <div className="text-sm font-bold text-zinc-800">{fmtRs(costs.total_manpower_cost_per_unit)}</div>
            </div>
            <div>
              <div className="text-[9px] text-zinc-400 uppercase font-semibold">Total Est. Internal Cost</div>
              <div className="text-sm font-bold text-amber-700">{fmtRs(costs.total_estimated_cost_per_unit)}</div>
            </div>
          </div>
          <p className="text-[9px] text-zinc-400 italic">Advisory only — does not overwrite selling rates or master rates</p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════ */
/*  ITEM FORM                         */
/* ═══════════════════════════════════ */

const BLANK_ITEM = {
  name: '', trade_id: '', category: '', subcategory: '', activity_group: '',
  unit: 'Sqft', description: '',
  material_recipe: [], manpower_recipe: [],
  base_rate: 0, min_rate: 0, max_rate: 0, default_rate_type: 'base_rate',
  // Phase 1 — Execution & client-facing
  execution_type: 'in_house',
  basic_rate_breakup: '',
  inclusions: [], exclusions: [],
  specification: '', make: '',
  preferred_subcontractor_id: '',
};

const EXECUTION_OPTIONS = [
  { value: 'in_house',           label: 'In-House',                 hint: 'We execute. Material + manpower demand created.' },
  { value: 'sc_with_material',   label: 'Sub-Contract WITH material', hint: 'SC supplies labour + material. No mat demand, no MP demand. Vendor + WO required.' },
  { value: 'sc_without_material',label: 'Sub-Contract WITHOUT material', hint: 'SC labour only, we supply material. Mat demand yes, no MP demand. Vendor + WO required.' },
];

/* ═══════════════════════════════════ */
/*  AI RECIPE SUGGEST (Phase 4)        */
/* ═══════════════════════════════════ */

const AISuggest = ({ name, unit, tradeName, description, onApply, onMaterialCreated }) => {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState('confirm'); // confirm | loading | result
  const [estimate, setEstimate] = useState(null);
  const [rate, setRate] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [executionType, setExecutionType] = useState('in_house');
  const [addingMat, setAddingMat] = useState(null);

  const payload = { name, unit: unit || 'Sqft', trade_name: tradeName || '', description: description || '', execution_type: executionType };

  const openPanel = async () => {
    if (!name || name.trim().length < 3) { toast.error('Enter an item name first (min 3 chars)'); return; }
    setOpen(true); setStage('confirm'); setResult(null); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/studio/ai-suggest/estimate`, payload, { withCredentials: true });
      setEstimate(data.estimate); setRate(data.rate_limit);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not estimate AI cost');
      setOpen(false);
    }
    setBusy(false);
  };

  const runAI = async () => {
    setStage('loading'); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/studio/ai-suggest`, payload, { withCredentials: true });
      const first = (data.results || [])[0];
      if (!first) { toast.error('AI returned no suggestion'); setOpen(false); setBusy(false); return; }
      setResult(first); setRate(data.rate_limit); setStage('result');
      if (data.rate_limit?.warn) toast.warning(`AI usage ${data.rate_limit.used}/${data.rate_limit.limit} this hour`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI suggestion failed');
      setOpen(false);
    }
    setBusy(false);
  };

  const apply = () => {
    onApply(result, executionType);
    toast.success('AI suggestion applied — review & edit before saving');
    setOpen(false);
  };

  const quickAddMaterial = async (i) => {
    const m = result.material_recipe[i];
    setAddingMat(i);
    try {
      const { data } = await axios.post(`${API}/api/material-master`, {
        description: m.ai_material_name || m.material_name, unit: m.unit || 'Nos',
        category: result.category || 'Others', family: '',
      }, { withCredentials: true });
      setResult(prev => ({
        ...prev,
        material_recipe: prev.material_recipe.map((x, idx) => idx === i
          ? { ...x, matched: true, material_id: data.id, material_code: data.material_code, material_name: data.description, match_confidence: 1 }
          : x),
      }));
      toast.success(`Added ${data.material_code} to Material Master`);
      if (onMaterialCreated) onMaterialCreated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add material'); }
    setAddingMat(null);
  };

  return (
    <>
      <Button type="button" size="sm" onClick={openPanel} disabled={busy}
        className="bg-violet-600 hover:bg-violet-700 rounded-none text-xs gap-1" data-testid="ai-suggest-btn">
        {busy && stage === 'confirm' ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
        AI Suggest
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="ai-suggest-modal">
          <div className="bg-white w-full max-w-2xl max-h-[88vh] overflow-y-auto shadow-2xl border border-zinc-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-violet-50 sticky top-0">
              <div className="flex items-center gap-2 text-violet-800 font-bold text-sm">
                <Sparkle size={16} weight="fill" /> AI Recipe Builder
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-700" data-testid="ai-close"><XCircle size={20} /></button>
            </div>

            <div className="p-4 space-y-4">
              {/* CONFIRM / COST PREVIEW */}
              {stage === 'confirm' && (
                <div className="space-y-3" data-testid="ai-confirm">
                  <p className="text-xs text-zinc-600">Claude Sonnet 4.6 will suggest a full recipe (materials, manpower, trade, category, spec, inclusions/exclusions, make) for:</p>
                  <div className="bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs"><strong>{name}</strong> · {unit || 'Sqft'}{tradeName ? ` · ${tradeName}` : ''}</div>
                  <div data-testid="ai-exec-select">
                    <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">Execution Type</div>
                    <div className="grid grid-cols-3 gap-2">
                      {[['in_house', 'In-House', 'Material + Manpower'], ['sc_with_material', 'SC + Material', 'No recipes'], ['sc_without_material', 'SC (Labour)', 'Material only']].map(([val, label, hint]) => (
                        <button key={val} type="button" onClick={() => setExecutionType(val)}
                          className={`border px-2 py-1.5 text-left rounded-none ${executionType === val ? 'border-violet-500 bg-violet-50' : 'border-zinc-200'}`}
                          data-testid={`ai-exec-${val}`}>
                          <div className="text-[11px] font-semibold text-zinc-800">{label}</div>
                          <div className="text-[9px] text-zinc-400">{hint}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {estimate && (
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="border border-zinc-200 p-2"><div className="text-[10px] text-zinc-500 uppercase">Est. Tokens</div><div className="text-sm font-bold text-zinc-800" data-testid="ai-est-tokens">{estimate.est_total_tokens.toLocaleString()}</div></div>
                      <div className="border border-zinc-200 p-2"><div className="text-[10px] text-zinc-500 uppercase">Est. Cost</div><div className="text-sm font-bold text-emerald-700" data-testid="ai-est-cost">${estimate.est_cost_usd}</div></div>
                      <div className="border border-zinc-200 p-2"><div className="text-[10px] text-zinc-500 uppercase">Usage</div><div className="text-sm font-bold text-zinc-800" data-testid="ai-rate">{rate?.used}/{rate?.limit}</div></div>
                    </div>
                  )}
                  {rate?.warn && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5">You've used {rate.used} of {rate.limit} AI calls this hour. {rate.remaining} remaining.</div>}
                  <div className="text-[10px] text-zinc-400">Estimate is approximate. Suggestions are advisory and never auto-saved.</div>
                  <div className="flex gap-2 pt-1">
                    <Button onClick={runAI} size="sm" disabled={busy || rate?.remaining <= 0} className="bg-violet-600 hover:bg-violet-700 rounded-none text-xs gap-1" data-testid="ai-run">
                      <Sparkle size={14} weight="fill" /> Generate Recipe
                    </Button>
                    <Button onClick={() => setOpen(false)} size="sm" variant="outline" className="rounded-none text-xs">Cancel</Button>
                  </div>
                </div>
              )}

              {/* LOADING */}
              {stage === 'loading' && (
                <div className="py-10 flex flex-col items-center gap-3 text-violet-700" data-testid="ai-loading">
                  <CircleNotch size={32} className="animate-spin" />
                  <div className="text-xs">Generating recipe with Claude Sonnet 4.6…</div>
                </div>
              )}

              {/* RESULT / REVIEW */}
              {stage === 'result' && result && (
                <div className="space-y-3" data-testid="ai-result">
                  {(result.trade || result.category) && (
                    <div className="flex gap-2 text-[10px]" data-testid="ai-trade-category">
                      {result.trade && <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded">Trade: {result.trade}</span>}
                      {result.category && <span className="bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded">Category: {result.category}</span>}
                    </div>
                  )}
                  {result.specification && (
                    <div><div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">Specification</div><div className="text-xs text-zinc-700 bg-zinc-50 border border-zinc-200 p-2">{result.specification}</div></div>
                  )}
                  {result.make && (
                    <div><div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">Make / Brand</div><div className="text-xs text-zinc-700">{result.make}</div></div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {result.inclusions?.length > 0 && (
                      <div><div className="text-[10px] font-semibold text-emerald-600 uppercase mb-1">Inclusions ({result.inclusions.length})</div><ul className="text-[11px] text-zinc-600 list-disc pl-4 space-y-0.5">{result.inclusions.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                    )}
                    {result.exclusions?.length > 0 && (
                      <div><div className="text-[10px] font-semibold text-rose-600 uppercase mb-1">Exclusions ({result.exclusions.length})</div><ul className="text-[11px] text-zinc-600 list-disc pl-4 space-y-0.5">{result.exclusions.map((s, i) => <li key={i}>{s}</li>)}</ul></div>
                    )}
                  </div>
                  {result.material_recipe?.length > 0 && (
                    <div>
                      <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">Material Recipe ({result.material_recipe.length})</div>
                      <table className="w-full text-[11px]">
                        <thead><tr className="text-zinc-400 border-b border-zinc-100"><th className="text-left py-1">Material</th><th className="text-left">Unit</th><th className="text-right">Qty/Unit</th><th className="text-right">Wastage%</th><th className="text-center">Master</th></tr></thead>
                        <tbody>
                          {result.material_recipe.map((m, i) => (
                            <tr key={i} className="border-b border-zinc-50">
                              <td className="py-1 text-zinc-700">{m.material_name}{m.matched && m.ai_material_name !== m.material_name && <span className="text-[9px] text-zinc-400 block">AI: {m.ai_material_name}</span>}</td>
                              <td className="text-zinc-500">{m.unit}</td>
                              <td className="text-right text-zinc-700">{m.quantity_per_unit}</td>
                              <td className="text-right text-zinc-500">{m.wastage_percentage}</td>
                              <td className="text-center">{m.matched ? <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 rounded">{Math.round(m.match_confidence * 100)}%</span> : (
                                <button onClick={() => quickAddMaterial(i)} disabled={addingMat === i}
                                  className="text-[9px] bg-violet-600 hover:bg-violet-700 text-white px-1.5 py-0.5 rounded disabled:opacity-50"
                                  data-testid={`ai-add-material-${i}`} title="This material is not in your Material Master. Add it now?">
                                  {addingMat === i ? '…' : '+ Add'}
                                </button>
                              )}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {result.manpower_recipe?.length > 0 && (
                    <div>
                      <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">Manpower Recipe ({result.manpower_recipe.length})</div>
                      <table className="w-full text-[11px]">
                        <thead><tr className="text-zinc-400 border-b border-zinc-100"><th className="text-left py-1">Role</th><th className="text-left">Type</th><th className="text-right">Output/Day</th><th className="text-right">MP/Unit</th></tr></thead>
                        <tbody>
                          {result.manpower_recipe.map((mp, i) => (
                            <tr key={i} className="border-b border-zinc-50"><td className="py-1 text-zinc-700">{mp.role_name}</td><td className="text-zinc-500">{mp.labour_type}</td><td className="text-right text-zinc-700">{mp.output_per_day}</td><td className="text-right text-zinc-500">{mp.manpower_per_unit}</td></tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2 border-t border-zinc-100 sticky bottom-0 bg-white">
                    <Button onClick={apply} size="sm" className="bg-violet-600 hover:bg-violet-700 rounded-none text-xs gap-1" data-testid="ai-apply">
                      <CheckCircle size={14} weight="fill" /> Apply to Form
                    </Button>
                    <Button onClick={() => setOpen(false)} size="sm" variant="outline" className="rounded-none text-xs" data-testid="ai-discard">Discard</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ═══════════════════════════════════ */
/*  AI BACKFILL MODAL (Phase 4 — bulk)  */
/* ═══════════════════════════════════ */

const AIBackfillModal = ({ open, onClose, onApplied }) => {
  const [stage, setStage] = useState('scanning'); // scanning | empty | loading | review
  const [candidates, setCandidates] = useState([]);
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState({}); // { itemId: { mat: bool[], mp: bool[] } }
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStage('scanning'); setCandidates([]); setResults([]); setSel({});
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/studio/ai-backfill/scan`, { withCredentials: true });
        if (!data.candidates?.length) { setStage('empty'); return; }
        setCandidates(data.candidates); setStage('scan_done');
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Scan failed'); onClose();
      }
    })();
  }, [open]); // eslint-disable-line

  const runBatch = async () => {
    setStage('loading');
    try {
      const items = candidates.map(c => ({ name: c.name, unit: c.unit, trade_name: c.trade_name, description: '' }));
      const { data } = await axios.post(`${API}/api/studio/ai-suggest`, { items }, { withCredentials: true });
      const res = data.results || [];
      setResults(res);
      const initSel = {};
      candidates.forEach((c, i) => {
        initSel[c.id] = {
          mat: (res[i]?.material_recipe || []).map(() => true),
          mp: (res[i]?.manpower_recipe || []).map(() => true),
        };
      });
      setSel(initSel);
      setStage('review');
      if (data.rate_limit?.warn) toast.warning(`AI usage ${data.rate_limit.used}/${data.rate_limit.limit} this hour`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI suggestion failed');
      setStage('scan_done');
    }
  };

  const toggle = (itemId, kind, idx) => {
    setSel(s => {
      const cur = { ...(s[itemId] || { mat: [], mp: [] }) };
      const arr = [...(cur[kind] || [])]; arr[idx] = !arr[idx]; cur[kind] = arr;
      return { ...s, [itemId]: cur };
    });
  };

  const buildApplication = (cand, res) => {
    const s = sel[cand.id] || { mat: [], mp: [] };
    const app = { item_id: cand.id };
    if (cand.missing_material) {
      const mats = (res.material_recipe || []).filter((_, i) => s.mat[i]).map(m => ({
        material_id: m.material_id || '', material_name: m.material_name || '', material_code: m.material_code || '',
        unit: m.unit || '', quantity_per_unit: m.quantity_per_unit || 0, wastage_percentage: m.wastage_percentage || 0,
      }));
      if (mats.length) app.materials = mats;
    }
    if (cand.missing_manpower) {
      const mp = (res.manpower_recipe || []).filter((_, i) => s.mp[i]).map(x => ({
        labour_type: x.labour_type || 'Skilled', role_name: x.role_name || '',
        manpower_per_unit: x.manpower_per_unit || 0, output_per_day: x.output_per_day || 0,
        metric_input: x.metric_input || 'output_per_day', remarks: x.remarks || '',
      }));
      if (mp.length) app.manpower_entries = mp;
    }
    return app;
  };

  const doApply = async (applications, label) => {
    const valid = applications.filter(a => a.materials || a.manpower_entries);
    if (!valid.length) { toast.error('Nothing selected to apply'); return; }
    setApplying(true);
    try {
      const { data } = await axios.post(`${API}/api/studio/ai-backfill/apply`, { applications: valid }, { withCredentials: true });
      toast.success(`${data.updated_count} item(s) backfilled — ${label}`);
      // Remove applied items from the panel
      const appliedIds = new Set(valid.map(a => a.item_id));
      const remIdx = candidates.map((c, i) => (appliedIds.has(c.id) ? -1 : i)).filter(i => i >= 0);
      setCandidates(remIdx.map(i => candidates[i]));
      setResults(remIdx.map(i => results[i]));
      onApplied && onApplied();
      if (remIdx.length === 0) onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    }
    setApplying(false);
  };

  const acceptItem = (idx) => doApply([buildApplication(candidates[idx], results[idx])], candidates[idx].item_code);
  const acceptAll = () => doApply(candidates.map((c, i) => buildApplication(c, results[i])), 'All');

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="ai-backfill-modal">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl border border-zinc-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 bg-violet-50 sticky top-0 z-10">
          <div className="flex items-center gap-2 text-violet-800 font-bold text-sm">
            <Sparkle size={16} weight="fill" /> AI Backfill — Missing Recipes
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="backfill-close"><XCircle size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          {stage === 'scanning' && <div className="py-10 flex flex-col items-center gap-3 text-violet-700"><CircleNotch size={28} className="animate-spin" /><div className="text-xs">Scanning published items…</div></div>}

          {stage === 'empty' && <div className="py-10 text-center text-sm text-zinc-500" data-testid="backfill-empty">All published items already have material &amp; manpower recipes. Nothing to backfill.</div>}

          {stage === 'scan_done' && (
            <div className="space-y-3" data-testid="backfill-scan-done">
              <p className="text-xs text-zinc-600"><strong className="text-violet-700">{candidates.length}</strong> published item(s) are missing a material or manpower recipe. Generate suggestions for all of them in one batch (Claude Sonnet 4.6).</p>
              <div className="border border-zinc-200 max-h-60 overflow-y-auto">
                {candidates.map(c => (
                  <div key={c.id} className="flex items-center justify-between px-3 py-1.5 border-b border-zinc-50 text-xs">
                    <span className="text-zinc-700">{c.item_code} — {c.name}</span>
                    <span className="flex gap-1">
                      {c.missing_material && <span className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded">no material</span>}
                      {c.missing_manpower && <span className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded">no manpower</span>}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button onClick={runBatch} size="sm" className="bg-violet-600 hover:bg-violet-700 rounded-none text-xs gap-1" data-testid="backfill-generate">
                  <Sparkle size={14} weight="fill" /> Generate Suggestions ({candidates.length})
                </Button>
                <Button onClick={onClose} size="sm" variant="outline" className="rounded-none text-xs">Cancel</Button>
              </div>
            </div>
          )}

          {stage === 'loading' && <div className="py-10 flex flex-col items-center gap-3 text-violet-700"><CircleNotch size={28} className="animate-spin" /><div className="text-xs">Generating recipes for {candidates.length} item(s)…</div></div>}

          {stage === 'review' && (
            <div className="space-y-4" data-testid="backfill-review">
              <div className="flex items-center justify-between sticky top-0 bg-white py-1">
                <p className="text-xs text-zinc-500">{candidates.length} item(s) — review &amp; accept. Nothing is saved until you click Accept.</p>
                <Button onClick={acceptAll} size="sm" disabled={applying} className="bg-violet-600 hover:bg-violet-700 rounded-none text-xs gap-1" data-testid="backfill-accept-all">
                  <CheckCircle size={14} weight="fill" /> Accept All
                </Button>
              </div>
              {candidates.map((c, idx) => {
                const res = results[idx] || {};
                const s = sel[c.id] || { mat: [], mp: [] };
                return (
                  <div key={c.id} className="border border-zinc-200" data-testid={`backfill-item-${c.id}`}>
                    <div className="flex items-center justify-between px-3 py-2 bg-zinc-50 border-b border-zinc-100">
                      <span className="text-xs font-bold text-zinc-700">{c.item_code} — {c.name}</span>
                      <Button onClick={() => acceptItem(idx)} size="sm" disabled={applying} variant="outline" className="rounded-none text-[11px] border-violet-300 text-violet-700 hover:bg-violet-50" data-testid={`backfill-accept-${c.id}`}>Accept Selected</Button>
                    </div>
                    <div className="p-3 space-y-2">
                      {c.missing_material && (res.material_recipe || []).length > 0 && (
                        <div>
                          <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">Material Recipe</div>
                          {res.material_recipe.map((m, i) => (
                            <label key={i} className="flex items-center gap-2 text-[11px] py-0.5">
                              <input type="checkbox" checked={!!s.mat[i]} onChange={() => toggle(c.id, 'mat', i)} data-testid={`backfill-mat-${c.id}-${i}`} />
                              <span className="text-zinc-700 flex-1">{m.material_name}</span>
                              <span className="text-zinc-500">{m.quantity_per_unit} {m.unit} · {m.wastage_percentage}% waste</span>
                              {m.matched ? <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1 rounded">{Math.round(m.match_confidence * 100)}%</span> : <span className="text-[9px] bg-zinc-100 text-zinc-500 px-1 rounded">new</span>}
                            </label>
                          ))}
                        </div>
                      )}
                      {c.missing_manpower && (res.manpower_recipe || []).length > 0 && (
                        <div>
                          <div className="text-[10px] font-semibold text-zinc-500 uppercase mb-1">Manpower Recipe</div>
                          {res.manpower_recipe.map((mp, i) => (
                            <label key={i} className="flex items-center gap-2 text-[11px] py-0.5">
                              <input type="checkbox" checked={!!s.mp[i]} onChange={() => toggle(c.id, 'mp', i)} data-testid={`backfill-mp-${c.id}-${i}`} />
                              <span className="text-zinc-700 flex-1">{mp.role_name} ({mp.labour_type})</span>
                              <span className="text-zinc-500">{mp.output_per_day}/day · {mp.manpower_per_unit} MP/unit</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ItemForm = ({ item, onSave, onCancel, trades, materials, onMaterialCreated }) => {
  const [form, setForm] = useState(item ? { ...BLANK_ITEM, ...item } : { ...BLANK_ITEM });
  const [dupes, setDupes] = useState([]);
  const [checking, setChecking] = useState(false);
  const isEdit = !!item?.id;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyAISuggestion = (res, execType) => {
    const matchedTrade = res.trade
      ? trades.find(t => (t.name || '').toLowerCase() === res.trade.toLowerCase())
        || trades.find(t => (t.name || '').toLowerCase().includes(res.trade.toLowerCase()) || res.trade.toLowerCase().includes((t.name || '').toLowerCase()))
      : null;
    setForm(f => ({
      ...f,
      trade_id: matchedTrade ? (matchedTrade.id || matchedTrade._id) : f.trade_id,
      category: res.category || f.category,
      execution_type: execType || res.execution_type || f.execution_type,
      specification: res.specification || f.specification,
      make: res.make || f.make,
      inclusions: (res.inclusions && res.inclusions.length) ? res.inclusions : f.inclusions,
      exclusions: (res.exclusions && res.exclusions.length) ? res.exclusions : f.exclusions,
      material_recipe: (res.material_recipe || []).map(m => ({
        material_id: m.material_id || '', material_name: m.material_name || '',
        material_code: m.material_code || '', unit: m.unit || '',
        quantity_per_unit: m.quantity_per_unit || 0, wastage_percentage: m.wastage_percentage || 0,
      })),
      manpower_recipe: (res.manpower_recipe || []).map(mp => ({
        labour_type: mp.labour_type || 'Skilled', role_name: mp.role_name || '',
        manpower_per_unit: mp.manpower_per_unit || 0, output_per_day: mp.output_per_day || 0,
        metric_input: mp.metric_input || 'output_per_day', remarks: mp.remarks || '',
      })),
    }));
  };

  const checkDupes = useCallback(async (name) => {
    if (name.length < 3 || isEdit) return;
    setChecking(true);
    try {
      const { data } = await axios.get(`${API}/api/studio/items/duplicate-check?name=${encodeURIComponent(name)}`, { withCredentials: true });
      setDupes(data.duplicates || []);
    } catch { setDupes([]); }
    setChecking(false);
  }, [isEdit]);

  return (
    <div className="space-y-4" data-testid="item-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-semibold text-zinc-500 uppercase">Item Name *</label>
            <AISuggest
              name={form.name}
              unit={form.unit}
              tradeName={(trades.find(t => t.id === form.trade_id) || {}).name || ''}
              description={form.description}
              onApply={applyAISuggestion}
              onMaterialCreated={onMaterialCreated}
            />
          </div>
          <input value={form.name} onChange={e => { set('name', e.target.value); checkDupes(e.target.value); }}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" placeholder="e.g. False Ceiling - Gypsum Board" data-testid="item-name" />
          {dupes.length > 0 && (
            <div className="mt-1.5 p-2 bg-amber-50 border border-amber-200 rounded text-[11px]" data-testid="dupe-warning">
              <div className="flex items-center gap-1 text-amber-700 font-semibold mb-1"><Warning size={14} /> Similar items found:</div>
              {dupes.map(d => <div key={d.id} className="text-amber-600">{d.item_code} — {d.name} ({d.similarity}% match, {d.status})</div>)}
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Trade *</label>
          <select value={form.trade_id} onChange={e => set('trade_id', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" data-testid="item-trade">
            <option value="">Select trade...</option>
            {trades.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Unit</label>
          <input value={form.unit} onChange={e => set('unit', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" data-testid="item-unit" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Category</label>
          <input value={form.category} onChange={e => set('category', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Subcategory</label>
          <input value={form.subcategory} onChange={e => set('subcategory', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Activity Group</label>
          <input value={form.activity_group} onChange={e => set('activity_group', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" placeholder="e.g. Ceiling Works" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Description</label>
          <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" />
        </div>
      </div>

      {/* Phase 1: Execution Type */}
      <div className="border-t border-zinc-100 pt-3">
        <h4 className="text-[11px] font-bold text-zinc-600 uppercase tracking-wide mb-2">Execution Type</h4>
        <div className="grid grid-cols-3 gap-2">
          {EXECUTION_OPTIONS.map(opt => (
            <button
              type="button"
              key={opt.value}
              onClick={() => set('execution_type', opt.value)}
              className={`text-left p-2 border text-[11px] transition ${form.execution_type === opt.value ? 'bg-blue-50 border-blue-400 text-blue-900' : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}
              data-testid={`exec-type-${opt.value}`}
            >
              <div className="font-bold">{opt.label}</div>
              <div className="text-[10px] mt-0.5 text-zinc-500">{opt.hint}</div>
            </button>
          ))}
        </div>
        {form.execution_type !== 'in_house' && (
          <div className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5">
            Sub-contracted items require <strong>Vendor assignment + Work Order</strong> instead of RFQ. Procurement flow uses WO as both quote-acceptance and contract.
          </div>
        )}
      </div>

      {/* Phase 1: Specification & Make */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Specification (client-facing)</label>
          <textarea value={form.specification} onChange={e => set('specification', e.target.value)} rows={2}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1"
            placeholder="e.g. Vitrified, polished, glazed, square edge"
            data-testid="item-specification" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Make / Brand (client-facing)</label>
          <input value={form.make} onChange={e => set('make', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1"
            placeholder="e.g. Kajaria / Somany"
            data-testid="item-make" />
        </div>
      </div>

      {/* Phase 1: Inclusions / Exclusions */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Inclusions (one per line — shown to client)</label>
          <textarea
            value={(form.inclusions || []).join('\n')}
            onChange={e => set('inclusions', e.target.value.split('\n'))}
            rows={3}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1 font-mono"
            placeholder="Tile&#10;Adhesive&#10;Grout&#10;Labour&#10;Skirting up to 4 inch"
            data-testid="item-inclusions"
          />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Exclusions (one per line — shown to client)</label>
          <textarea
            value={(form.exclusions || []).join('\n')}
            onChange={e => set('exclusions', e.target.value.split('\n'))}
            rows={3}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1 font-mono"
            placeholder="Anti-skid coating&#10;Custom edge profiling&#10;Tile above 1m wall height"
            data-testid="item-exclusions"
          />
        </div>
      </div>

      {/* Phase 1: Basic Rate Breakup (internal only) */}
      <div>
        <label className="text-[10px] font-semibold text-zinc-500 uppercase flex items-center gap-1">
          Basic Rate Breakup
          <span className="text-[9px] bg-zinc-100 text-zinc-600 px-1 py-0.5 rounded">INTERNAL ONLY</span>
        </label>
        <textarea
          value={form.basic_rate_breakup}
          onChange={e => set('basic_rate_breakup', e.target.value)}
          rows={4}
          className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1 font-mono"
          placeholder="Tile @ Rs 85/Sft&#10;Adhesive @ Rs 12/Sft&#10;Grout @ Rs 4/Sft&#10;Labour @ Rs 25/Sft&#10;Margin @ Rs 19/Sft"
          data-testid="item-basic-rate-breakup"
        />
        <div className="text-[10px] text-zinc-500 mt-1">
          Free-form internal note. Client only sees the final basic rate within the item description/specifications, not this breakup.
        </div>
      </div>

      {/* Rate fields */}
      <div>
        <h4 className="text-[11px] font-bold text-zinc-600 uppercase tracking-wide mb-2">Rate Structure</h4>
        <div className="grid grid-cols-4 gap-3">
          {['base_rate', 'min_rate', 'max_rate'].map(f => (
            <div key={f}>
              <label className="text-[10px] font-semibold text-zinc-500 uppercase">{f.replace(/_/g, ' ')}</label>
              <input type="number" value={form[f]} onChange={e => set(f, parseFloat(e.target.value) || 0)}
                className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" />
            </div>
          ))}
          <div>
            <label className="text-[10px] font-semibold text-zinc-500 uppercase">Default Rate Type</label>
            <select value={form.default_rate_type} onChange={e => set('default_rate_type', e.target.value)}
              className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1">
              <option value="base_rate">Base Rate</option>
              <option value="min_rate">Min Rate</option>
              <option value="max_rate">Max Rate</option>
            </select>
          </div>
        </div>
      </div>

      {/* Recipes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2 border-t border-zinc-100">
        <MaterialRecipeEditor recipes={form.material_recipe || []} onChange={v => set('material_recipe', v)} materials={materials} onMaterialCreated={onMaterialCreated} />
        <ManpowerRecipeEditor recipes={form.manpower_recipe || []} onChange={v => set('manpower_recipe', v)} />
      </div>

      {/* Cost Calculator */}
      <CostCalculator materialRecipe={form.material_recipe} manpowerRecipe={form.manpower_recipe} />

      <div className="flex gap-2 pt-3 border-t border-zinc-100">
        <Button onClick={() => onSave(form)} size="sm" className="bg-[#1e3a5f] hover:bg-[#162d4a] rounded-none text-xs" data-testid="save-item">
          {isEdit ? 'Update' : 'Save Draft'}
        </Button>
        <Button onClick={onCancel} size="sm" variant="outline" className="rounded-none text-xs" data-testid="cancel-item">Cancel</Button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════ */
/*  ASSEMBLY FORM                     */
/* ═══════════════════════════════════ */

const BLANK_ASM = {
  name: '', trade_id: '', category: '', subcategory: '', activity_group: '',
  unit: 'Sqft', description: '',
  children: [], direct_material_recipe: [], direct_manpower_recipe: [],
  base_rate: 0, min_rate: 0, max_rate: 0, default_rate_type: 'base_rate',
};

const AssemblyForm = ({ assembly, onSave, onCancel, trades, materials, studioItems, studioAssemblies, onMaterialCreated }) => {
  const [form, setForm] = useState(assembly ? { ...BLANK_ASM, ...assembly } : { ...BLANK_ASM });
  const isEdit = !!assembly?.id;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyAISuggestionAsm = (res, execType) => {
    const matchedTrade = res.trade
      ? trades.find(t => (t.name || '').toLowerCase() === res.trade.toLowerCase())
        || trades.find(t => (t.name || '').toLowerCase().includes(res.trade.toLowerCase()))
      : null;
    setForm(f => ({
      ...f,
      trade_id: matchedTrade ? (matchedTrade.id || matchedTrade._id) : f.trade_id,
      category: res.category || f.category,
      description: res.specification || f.description,
      direct_material_recipe: (res.material_recipe || []).map(m => ({
        material_id: m.material_id || '', material_name: m.material_name || '',
        material_code: m.material_code || '', unit: m.unit || '',
        quantity_per_unit: m.quantity_per_unit || 0, wastage_percentage: m.wastage_percentage || 0,
      })),
      direct_manpower_recipe: (res.manpower_recipe || []).map(mp => ({
        labour_type: mp.labour_type || 'Skilled', role_name: mp.role_name || '',
        manpower_per_unit: mp.manpower_per_unit || 0, output_per_day: mp.output_per_day || 0,
        metric_input: mp.metric_input || 'output_per_day', remarks: mp.remarks || '',
      })),
    }));
  };

  const addChild = (type) => {
    set('children', [...(form.children || []), { type, ref_id: '', quantity_ratio: 1.0 }]);
  };
  const removeChild = (i) => set('children', (form.children || []).filter((_, idx) => idx !== i));
  const updateChild = (i, field, value) => {
    const next = [...(form.children || [])];
    next[i] = { ...next[i], [field]: value };
    set('children', next);
  };

  const childOptions = (type) => {
    if (type === 'item') return studioItems.filter(i => i.status !== 'draft' || true);
    return studioAssemblies.filter(a => a.id !== assembly?.id);
  };

  return (
    <div className="space-y-4" data-testid="assembly-form">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-semibold text-zinc-500 uppercase">Assembly Name *</label>
            <AISuggest
              name={form.name}
              unit={form.unit}
              tradeName={(trades.find(t => t.id === form.trade_id) || {}).name || ''}
              description={form.description}
              onApply={applyAISuggestionAsm}
              onMaterialCreated={onMaterialCreated}
            />
          </div>
          <input value={form.name} onChange={e => set('name', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" data-testid="asm-name" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Trade *</label>
          <select value={form.trade_id} onChange={e => set('trade_id', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" data-testid="asm-trade">
            <option value="">Select trade...</option>
            {trades.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Unit</label>
          <input value={form.unit} onChange={e => set('unit', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Category</label>
          <input value={form.category} onChange={e => set('category', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Activity Group</label>
          <input value={form.activity_group} onChange={e => set('activity_group', e.target.value)}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" />
        </div>
        <div className="col-span-2">
          <label className="text-[10px] font-semibold text-zinc-500 uppercase">Description</label>
          <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
            className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" />
        </div>
      </div>

      {/* Rate fields */}
      <div>
        <h4 className="text-[11px] font-bold text-zinc-600 uppercase tracking-wide mb-2">Rate Structure</h4>
        <div className="grid grid-cols-4 gap-3">
          {['base_rate', 'min_rate', 'max_rate'].map(f => (
            <div key={f}>
              <label className="text-[10px] font-semibold text-zinc-500 uppercase">{f.replace(/_/g, ' ')}</label>
              <input type="number" value={form[f]} onChange={e => set(f, parseFloat(e.target.value) || 0)}
                className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1" />
            </div>
          ))}
          <div>
            <label className="text-[10px] font-semibold text-zinc-500 uppercase">Default Rate Type</label>
            <select value={form.default_rate_type} onChange={e => set('default_rate_type', e.target.value)}
              className="w-full border border-zinc-200 rounded px-3 py-2 text-xs mt-1">
              <option value="base_rate">Base Rate</option>
              <option value="min_rate">Min Rate</option>
              <option value="max_rate">Max Rate</option>
            </select>
          </div>
        </div>
      </div>

      {/* Children */}
      <div className="pt-2 border-t border-zinc-100">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[11px] font-bold text-zinc-600 uppercase tracking-wide">Child Components</h4>
          <div className="flex gap-2">
            <button onClick={() => addChild('item')} className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1" data-testid="add-child-item">
              <Plus size={12} /> Add Item
            </button>
            <button onClick={() => addChild('assembly')} className="text-[10px] text-violet-600 hover:text-violet-800 font-semibold flex items-center gap-1" data-testid="add-child-assembly">
              <Plus size={12} /> Add Assembly
            </button>
          </div>
        </div>
        {(form.children || []).length === 0 && <p className="text-[11px] text-zinc-400 italic">No children added</p>}
        {(form.children || []).map((ch, i) => (
          <div key={i} className="flex items-center gap-2 mb-2 p-2 bg-zinc-50 rounded border border-zinc-100">
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${ch.type === 'item' ? 'bg-cyan-100 text-cyan-700' : 'bg-violet-100 text-violet-700'}`}>
              {ch.type}
            </span>
            <select value={ch.ref_id} onChange={e => updateChild(i, 'ref_id', e.target.value)}
              className="flex-1 border border-zinc-200 rounded px-2 py-1.5 text-[11px]" data-testid={`child-select-${i}`}>
              <option value="">Select {ch.type}...</option>
              {childOptions(ch.type).map(o => (
                <option key={o.id} value={o.id}>{o.item_code || o.assembly_code} — {o.name}</option>
              ))}
            </select>
            <div className="flex items-center gap-1">
              <label className="text-[10px] text-zinc-500">Qty:</label>
              <input type="number" value={ch.quantity_ratio} onChange={e => updateChild(i, 'quantity_ratio', parseFloat(e.target.value) || 1)}
                className="w-16 border border-zinc-200 rounded px-2 py-1.5 text-[11px] text-right" step="0.1" data-testid={`child-qty-${i}`} />
            </div>
            <button onClick={() => removeChild(i)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button>
          </div>
        ))}
      </div>

      {/* Direct recipes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 pt-2 border-t border-zinc-100">
        <MaterialRecipeEditor recipes={form.direct_material_recipe || []} onChange={v => set('direct_material_recipe', v)} materials={materials} onMaterialCreated={onMaterialCreated} />
        <ManpowerRecipeEditor recipes={form.direct_manpower_recipe || []} onChange={v => set('direct_manpower_recipe', v)} />
      </div>

      {/* Cost Calculator for assemblies — uses direct recipes for now */}
      <CostCalculator materialRecipe={form.direct_material_recipe} manpowerRecipe={form.direct_manpower_recipe} />

      <div className="flex gap-2 pt-3 border-t border-zinc-100">
        <Button onClick={() => onSave(form)} size="sm" className="bg-[#1e3a5f] hover:bg-[#162d4a] rounded-none text-xs" data-testid="save-assembly">
          {isEdit ? 'Update' : 'Save Draft'}
        </Button>
        <Button onClick={onCancel} size="sm" variant="outline" className="rounded-none text-xs">Cancel</Button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════ */
/*  DETAIL VIEW                       */
/* ═══════════════════════════════════ */

const RecipePreview = ({ label, materials, manpower }) => (
  <div className="mt-3">
    <h5 className="text-[10px] font-bold text-zinc-500 uppercase mb-1">{label}</h5>
    {(materials || []).length > 0 && (
      <div className="mb-2">
        <span className="text-[10px] text-zinc-400">Materials:</span>
        <table className="w-full text-[11px] mt-1">
          <thead><tr className="border-b border-zinc-100">
            <th className="text-left py-1 text-zinc-400 font-semibold">Material</th>
            <th className="text-right py-1 text-zinc-400 font-semibold w-20">Qty/Unit</th>
            <th className="text-right py-1 text-zinc-400 font-semibold w-20">Wastage%</th>
          </tr></thead>
          <tbody>{materials.map((m, i) => (
            <tr key={i} className="border-b border-zinc-50">
              <td className="py-1 text-zinc-700">{m.material_name}{m.material_code ? ` (${m.material_code})` : ''}</td>
              <td className="py-1 text-right text-zinc-600">{m.quantity_per_unit}</td>
              <td className="py-1 text-right text-zinc-600">{m.wastage_percentage}%</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    )}
    {(manpower || []).length > 0 && (
      <div>
        <span className="text-[10px] text-zinc-400">Manpower:</span>
        <table className="w-full text-[11px] mt-1">
          <thead><tr className="border-b border-zinc-100">
            <th className="text-left py-1 text-zinc-400 font-semibold">Role</th>
            <th className="text-left py-1 text-zinc-400 font-semibold">Type</th>
            <th className="text-right py-1 text-zinc-400 font-semibold w-20">MP/Unit</th>
          </tr></thead>
          <tbody>{manpower.map((mp, i) => (
            <tr key={i} className="border-b border-zinc-50">
              <td className="py-1 text-zinc-700">{mp.role_name}</td>
              <td className="py-1 text-zinc-600">{mp.labour_type}</td>
              <td className="py-1 text-right text-zinc-600">{mp.manpower_per_unit}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    )}
    {(materials || []).length === 0 && (manpower || []).length === 0 && (
      <p className="text-[11px] text-zinc-400 italic">No recipe defined</p>
    )}
  </div>
);

/* ═══════════════════════════════════ */
/*  MAIN PAGE                         */
/* ═══════════════════════════════════ */

const TABS = [
  { key: 'items', label: 'Items', icon: Package },
  { key: 'assemblies', label: 'Assemblies', icon: Cube },
  { key: 'approvals', label: 'Approvals', icon: CheckCircle },
  { key: 'published', label: 'Published', icon: ArrowRight },
];

export default function MasterStudio() {
  const [tab, setTab] = useState('items');
  const [items, setItems] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [approvalQueue, setApprovalQueue] = useState([]);
  const [publishLog, setPublishLog] = useState([]);
  const [trades, setTrades] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [editingItem, setEditingItem] = useState(null); // null=list, {}=new, {id:..}=edit
  const [editingAsm, setEditingAsm] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const bulkFileInputRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);

  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/api/studio/bulk-import/template`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'MasterStudio_BulkImport_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download template');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/studio/bulk-import`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { created_count, error_count, total_items, errors } = res.data;
      if (error_count > 0) {
        const preview = (errors || []).slice(0, 3).map(er => `Row ${er.row}: ${er.error}`).join('\n');
        toast.warning(`Imported ${created_count}/${total_items} — ${error_count} errors.\n${preview}`, { duration: 8000 });
      } else {
        toast.success(`Imported ${created_count} items into Master Studio (as drafts/approved)`);
      }
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk import failed');
    } finally {
      setBulkUploading(false);
      if (bulkFileInputRef.current) bulkFileInputRef.current.value = '';
    }
  };

  const fetchAll = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const qs = params.toString() ? `?${params.toString()}` : '';

      const [itemsRes, asmRes, tradesRes, matRes] = await Promise.all([
        axios.get(`${API}/api/studio/items${qs}`, { withCredentials: true }),
        axios.get(`${API}/api/studio/assemblies${qs}`, { withCredentials: true }),
        axios.get(`${API}/api/trades`, { withCredentials: true }),
        axios.get(`${API}/api/material-master`, { withCredentials: true }),
      ]);
      setItems(itemsRes.data || []);
      setAssemblies(asmRes.data || []);
      setTrades(tradesRes.data || []);
      setMaterials(matRes.data || []);
    } catch {}
  }, [search, statusFilter]);

  const fetchApprovals = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/studio/approval-queue`, { withCredentials: true });
      setApprovalQueue(data || []);
    } catch {}
  }, []);

  const fetchPublishLog = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/studio/publish-log`, { withCredentials: true });
      setPublishLog(data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (tab === 'approvals') fetchApprovals(); }, [tab, fetchApprovals]);
  useEffect(() => { if (tab === 'published') fetchPublishLog(); }, [tab, fetchPublishLog]);

  /* ── Item actions ── */
  const saveItem = async (form) => {
    try {
      if (form.id) {
        await axios.put(`${API}/api/studio/items/${form.id}`, form, { withCredentials: true });
        toast.success('Item updated.');
      } else {
        await axios.post(`${API}/api/studio/items`, form, { withCredentials: true });
        toast.success('Item created.');
      }
      setEditingItem(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed.'); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this draft item?')) return;
    try {
      await axios.delete(`${API}/api/studio/items/${id}`, { withCredentials: true });
      toast.success('Deleted.'); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed.'); }
  };

  /* ── Assembly actions ── */
  const saveAssembly = async (form) => {
    try {
      if (form.id) {
        await axios.put(`${API}/api/studio/assemblies/${form.id}`, form, { withCredentials: true });
        toast.success('Assembly updated.');
      } else {
        await axios.post(`${API}/api/studio/assemblies`, form, { withCredentials: true });
        toast.success('Assembly created.');
      }
      setEditingAsm(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed.'); }
  };

  const deleteAssembly = async (id) => {
    if (!window.confirm('Delete this draft assembly?')) return;
    try {
      await axios.delete(`${API}/api/studio/assemblies/${id}`, { withCredentials: true });
      toast.success('Deleted.'); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed.'); }
  };

  /* ── Workflow actions ── */
  const workflowAction = async (entityType, id, action, remarks = '') => {
    try {
      await axios.post(`${API}/api/studio/${entityType}/${id}/${action}`, { remarks }, { withCredentials: true });
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} successful.`);
      fetchAll(); fetchApprovals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed.'); }
  };

  const publishAction = async (entityType, id) => {
    if (!window.confirm('Publish this item to ERP? A new versioned record will be created.')) return;
    try {
      const { data } = await axios.post(`${API}/api/studio/${entityType}/${id}/publish`, {}, { withCredentials: true });
      toast.success(data.message || 'Published.');
      fetchAll(); fetchPublishLog();
    } catch (e) { toast.error(e.response?.data?.detail || 'Publish failed.'); }
  };

  /* ── Item actions row ── */
  const ActionButtons = ({ item, entityType }) => {
    const s = item.status;
    return (
      <div className="flex gap-1">
        {s === 'draft' && (
          <>
            <button onClick={() => entityType === 'items' ? setEditingItem(item) : setEditingAsm(item)}
              className="p-1 text-zinc-400 hover:text-zinc-700" title="Edit" data-testid={`edit-${item.id}`}><Pencil size={14} /></button>
            <button onClick={() => workflowAction(entityType, item.id, 'submit')}
              className="p-1 text-blue-400 hover:text-blue-700" title="Submit for Review" data-testid={`submit-${item.id}`}><PaperPlaneTilt size={14} /></button>
            <button onClick={() => entityType === 'items' ? deleteItem(item.id) : deleteAssembly(item.id)}
              className="p-1 text-red-400 hover:text-red-600" title="Delete" data-testid={`delete-${item.id}`}><Trash size={14} /></button>
          </>
        )}
        {s === 'under_review' && (
          <>
            <button onClick={() => workflowAction(entityType, item.id, 'approve')}
              className="p-1 text-emerald-500 hover:text-emerald-700" title="Approve" data-testid={`approve-${item.id}`}><CheckCircle size={14} weight="fill" /></button>
            <button onClick={() => { const r = prompt('Rejection reason:'); if (r) workflowAction(entityType, item.id, 'reject', r); }}
              className="p-1 text-red-400 hover:text-red-600" title="Reject" data-testid={`reject-${item.id}`}><XCircle size={14} /></button>
          </>
        )}
        {s === 'approved' && (
          <button onClick={() => publishAction(entityType, item.id)}
            className="p-1 text-blue-500 hover:text-blue-700" title="Publish to ERP" data-testid={`publish-${item.id}`}><ArrowRight size={14} weight="bold" /></button>
        )}
        {(s === 'published' || s === 'approved') && (
          <button onClick={() => workflowAction(entityType, item.id, 'revise')}
            className="p-1 text-violet-500 hover:text-violet-700" title="Revise (new version cycle)" data-testid={`revise-${item.id}`}><ArrowCounterClockwise size={14} /></button>
        )}
      </div>
    );
  };

  /* ── Render ── */
  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="master-studio">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-bold text-zinc-900">Master Studio</h1>
          <p className="text-[11px] text-zinc-400">Create, review, and publish line items and assemblies</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-zinc-200">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => { setTab(t.key); setEditingItem(null); setEditingAsm(null); }}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${active ? 'border-[#1e3a5f] text-[#1e3a5f]' : 'border-transparent text-zinc-400 hover:text-zinc-600'}`}
              data-testid={`tab-${t.key}`}>
              <Icon size={15} weight={active ? 'fill' : 'regular'} /> {t.label}
              {t.key === 'approvals' && approvalQueue.length > 0 && (
                <span className="bg-amber-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{approvalQueue.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Items Tab ── */}
      {tab === 'items' && (
        editingItem !== null ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-5">
            <h2 className="text-sm font-bold text-zinc-800 mb-4">{editingItem.id ? 'Edit Item' : 'New Item'}</h2>
            <ItemForm item={editingItem} onSave={saveItem} onCancel={() => setEditingItem(null)} trades={trades} materials={materials} onMaterialCreated={fetchAll} />
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-zinc-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search items..."
                    className="border border-zinc-200 rounded pl-8 pr-3 py-2 text-xs w-56" data-testid="search-items" />
                </div>
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                  className="border border-zinc-200 rounded px-3 py-2 text-xs" data-testid="status-filter">
                  <option value="">All Status</option>
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  ref={bulkFileInputRef}
                  onChange={handleBulkUpload}
                  style={{ display: 'none' }}
                  data-testid="bulk-upload-input"
                />
                <Button
                  onClick={handleDownloadTemplate}
                  size="sm"
                  variant="outline"
                  className="rounded-none text-xs border-zinc-300"
                  data-testid="download-template-btn"
                >
                  <DownloadSimple size={14} className="mr-1" /> Template
                </Button>
                <Button
                  onClick={() => bulkFileInputRef.current?.click()}
                  size="sm"
                  variant="outline"
                  className="rounded-none text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  disabled={bulkUploading}
                  data-testid="bulk-import-btn"
                >
                  <UploadSimple size={14} className="mr-1" /> {bulkUploading ? 'Importing…' : 'Bulk Import'}
                </Button>
                <Button
                  onClick={() => setBackfillOpen(true)}
                  size="sm"
                  variant="outline"
                  className="rounded-none text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="ai-backfill-btn"
                >
                  <Sparkle size={14} weight="fill" className="mr-1" /> AI Backfill Missing Recipes
                </Button>
                <Button onClick={() => setEditingItem({})} size="sm" className="bg-[#1e3a5f] hover:bg-[#162d4a] rounded-none text-xs" data-testid="create-item-btn">
                  <Plus size={14} className="mr-1" /> New Item
                </Button>
              </div>
            </div>
            <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Code</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Name</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Trade</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Unit</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Recipes</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Status</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-zinc-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-400">No items found</td></tr>}
                  {items.map(i => (
                    <tr key={i.id} className="border-b border-zinc-50 hover:bg-zinc-50/50" data-testid={`item-row-${i.id}`}>
                      <td className="px-4 py-2.5 font-mono text-zinc-500">{i.item_code}</td>
                      <td className="px-4 py-2.5 font-medium text-zinc-800">{i.name}</td>
                      <td className="px-4 py-2.5 text-zinc-600">{i.trade_name}</td>
                      <td className="px-4 py-2.5 text-zinc-600">{i.unit}</td>
                      <td className="px-4 py-2.5 text-zinc-500">
                        {(i.material_recipe?.length || 0)}M / {(i.manpower_recipe?.length || 0)}L
                      </td>
                      <td className="px-4 py-2.5"><Badge status={i.status} /></td>
                      <td className="px-4 py-2.5 text-right"><ActionButtons item={i} entityType="items" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* ── Assemblies Tab ── */}
      {tab === 'assemblies' && (
        editingAsm !== null ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-5">
            <h2 className="text-sm font-bold text-zinc-800 mb-4">{editingAsm.id ? 'Edit Assembly' : 'New Assembly'}</h2>
            <AssemblyForm assembly={editingAsm} onSave={saveAssembly} onCancel={() => setEditingAsm(null)}
              trades={trades} materials={materials} studioItems={items} studioAssemblies={assemblies} onMaterialCreated={fetchAll} />
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-zinc-400" />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search assemblies..."
                    className="border border-zinc-200 rounded pl-8 pr-3 py-2 text-xs w-56" data-testid="search-assemblies" />
                </div>
              </div>
              <Button onClick={() => setEditingAsm({})} size="sm" className="bg-[#1e3a5f] hover:bg-[#162d4a] rounded-none text-xs" data-testid="create-assembly-btn">
                <Plus size={14} className="mr-1" /> New Assembly
              </Button>
            </div>
            <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Code</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Name</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Trade</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Children</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Agg. Recipes</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Status</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-zinc-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assemblies.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-400">No assemblies found</td></tr>}
                  {assemblies.map(a => (
                    <tr key={a.id} className="border-b border-zinc-50 hover:bg-zinc-50/50" data-testid={`asm-row-${a.id}`}>
                      <td className="px-4 py-2.5 font-mono text-zinc-500">{a.assembly_code}</td>
                      <td className="px-4 py-2.5 font-medium text-zinc-800">{a.name}</td>
                      <td className="px-4 py-2.5 text-zinc-600">{a.trade_name}</td>
                      <td className="px-4 py-2.5 text-zinc-500">{a.children?.length || 0}</td>
                      <td className="px-4 py-2.5 text-zinc-500">
                        {(a.aggregated_material_recipe?.length || 0)}M / {(a.aggregated_manpower_recipe?.length || 0)}L
                      </td>
                      <td className="px-4 py-2.5"><Badge status={a.status} /></td>
                      <td className="px-4 py-2.5 text-right"><ActionButtons item={a} entityType="assemblies" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* ── Approvals Tab ── */}
      {tab === 'approvals' && (
        <div className="space-y-3">
          {approvalQueue.length === 0 && (
            <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-zinc-400 text-sm">
              No items pending approval
            </div>
          )}
          {approvalQueue.map(q => (
            <div key={q.id} className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={`approval-${q.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${q.entity_type === 'items' ? 'bg-cyan-100 text-cyan-700' : 'bg-violet-100 text-violet-700'}`}>
                    {q.entity_type === 'items' ? 'Item' : 'Assembly'}
                  </span>
                  <span className="font-mono text-[11px] text-zinc-500">{q.item_code || q.assembly_code}</span>
                  <span className="text-sm font-semibold text-zinc-800">{q.name}</span>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => workflowAction(q.entity_type, q.id, 'approve')}
                    size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-none text-xs" data-testid={`approve-queue-${q.id}`}>
                    <CheckCircle size={14} className="mr-1" /> Approve
                  </Button>
                  <Button onClick={() => { const r = prompt('Rejection reason:'); if (r) workflowAction(q.entity_type, q.id, 'reject', r); }}
                    size="sm" variant="outline" className="rounded-none text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-queue-${q.id}`}>
                    <XCircle size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>
              <div className="text-[11px] text-zinc-500">
                Trade: {q.trade_name} | Unit: {q.unit} | Category: {q.category || '—'} | Activity Group: {q.activity_group || '—'}
              </div>
              <RecipePreview label="Recipe"
                materials={q.material_recipe || q.aggregated_material_recipe || []}
                manpower={q.manpower_recipe || q.aggregated_manpower_recipe || []} />
              {q.approval_history?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-zinc-100">
                  <span className="text-[10px] text-zinc-400 flex items-center gap-1"><ClockCounterClockwise size={12} /> History:</span>
                  {q.approval_history.map((h, i) => (
                    <div key={i} className="text-[10px] text-zinc-500 ml-4">{h.action} by {h.by_name} — {h.remarks || 'no remarks'}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Published Tab ── */}
      {tab === 'published' && (
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Source</th>
                <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Source Code</th>
                <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">ERP Code</th>
                <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Version</th>
                <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Published By</th>
                <th className="text-left px-4 py-2.5 font-semibold text-zinc-500">Date</th>
              </tr>
            </thead>
            <tbody>
              {publishLog.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-zinc-400">No published items yet</td></tr>}
              {publishLog.map(l => (
                <tr key={l.id} className="border-b border-zinc-50" data-testid={`pub-row-${l.id}`}>
                  <td className="px-4 py-2.5">
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${l.source_type === 'item' ? 'bg-cyan-100 text-cyan-700' : 'bg-violet-100 text-violet-700'}`}>
                      {l.source_type}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-zinc-600">{l.source_code}</td>
                  <td className="px-4 py-2.5 font-mono text-blue-600 font-semibold">{l.erp_item_code}</td>
                  <td className="px-4 py-2.5 text-zinc-600">v{l.version}</td>
                  <td className="px-4 py-2.5 text-zinc-600">{l.published_by_name}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{l.published_at ? new Date(l.published_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AIBackfillModal open={backfillOpen} onClose={() => setBackfillOpen(false)} onApplied={fetchAll} />
    </div>
  );
}
